// src/services/webrtcService.js

const RTC_CONFIG = {
    iceServers: [
        { urls: 'stun:stun.l.google.com:19302' },
        { urls: 'stun:stun1.l.google.com:19302' },
        { urls: 'stun:stun2.l.google.com:19302' },
        // Add TURN servers for better connectivity
        {
            urls: 'turn:openrelay.metered.ca:80',
            username: 'openrelayproject',
            credential: 'openrelayproject'
        },
        {
            urls: 'turn:openrelay.metered.ca:443',
            username: 'openrelayproject',
            credential: 'openrelayproject'
        }
    ]
};

export class WebRTCService {
    constructor() {
        this.peerConnection = null;
        this.localStream = null;
        this.remoteStream = null;
        this.isMuted = false;
        this.isInitialized = false;
    }

    // Check if microphone permission is granted
    async checkMicrophonePermission() {
        try {
            // Check if we already have permission
            const permissionStatus = await navigator.permissions.query({ name: 'microphone' });
            return permissionStatus.state === 'granted';
        } catch (err) {
            // permissions API might not be supported in all browsers
            console.warn('Permissions API not supported, will try to request directly');
            return false;
        }
    }

    // Request microphone permission explicitly
    async requestMicrophonePermission() {
        try {
            // Try to get a temporary stream to trigger permission request
            const tempStream = await navigator.mediaDevices.getUserMedia({ audio: true, video: false });
            // Stop the temporary stream immediately
            tempStream.getTracks().forEach(track => track.stop());
            return true;
        } catch (err) {
            console.error('Microphone permission denied:', err);
            return false;
        }
    }

    // Initialize local microphone audio with better error handling
    async startLocalStream(withVideo = false) {
        try {
            // Check if getUserMedia is supported
            if (!navigator.mediaDevices || !navigator.mediaDevices.getUserMedia) {
                throw new Error('Your browser does not support microphone access. Please use a modern browser.');
            }

            // Request permission first
            const hasPermission = await this.requestMicrophonePermission();
            if (!hasPermission) {
                throw new Error('Microphone access denied. Please grant microphone permission in your browser settings.');
            }

            // Get the actual stream
            this.localStream = await navigator.mediaDevices.getUserMedia({
                audio: {
                    echoCancellation: true,
                    noiseSuppression: true,
                    autoGainControl: true
                },
                video: withVideo ? {
                    width: { ideal: 640 },
                    height: { ideal: 480 }
                } : false
            });

            this.isInitialized = true;
            return this.localStream;
        } catch (err) {
            console.error('Error accessing microphone:', err);

            // Provide user-friendly error messages
            let errorMessage = 'Unable to access microphone. ';
            if (err.name === 'NotAllowedError' || err.name === 'PermissionDeniedError') {
                errorMessage += 'Please grant microphone permission in your browser settings and refresh the page.';
            } else if (err.name === 'NotFoundError' || err.name === 'DevicesNotFoundError') {
                errorMessage += 'No microphone found. Please connect a microphone and try again.';
            } else if (err.name === 'NotReadableError') {
                errorMessage += 'Your microphone is busy or not responding. Please check your microphone connection.';
            } else {
                errorMessage += err.message || 'Please check your microphone settings.';
            }

            throw new Error(errorMessage);
        }
    }

    // Create RTCPeerConnection and bind event listeners
    createPeerConnection(onIceCandidate, onTrack) {
        if (this.peerConnection) {
            this.peerConnection.close();
            this.peerConnection = null;
        }

        try {
            this.peerConnection = new RTCPeerConnection(RTC_CONFIG);

            // Add local tracks to connection
            if (this.localStream) {
                this.localStream.getTracks().forEach(track => {
                    this.peerConnection.addTrack(track, this.localStream);
                });
            }

            // Handle incoming ICE Candidates
            this.peerConnection.onicecandidate = (event) => {
                if (event.candidate) {
                    onIceCandidate(event.candidate.toJSON());
                }
            };

            // Handle incoming remote audio stream
            this.peerConnection.ontrack = (event) => {
                this.remoteStream = event.streams[0];
                onTrack(this.remoteStream);
            };

            // Handle connection state changes
            this.peerConnection.onconnectionstatechange = () => {
                const state = this.peerConnection.connectionState;
                console.log('WebRTC Connection state:', state);

                if (state === 'failed' || state === 'disconnected' || state === 'closed') {
                    // Don't cleanup automatically, let the caller handle it
                    console.warn('WebRTC connection state:', state);
                }
            };

            // Handle ICE connection state changes
            this.peerConnection.oniceconnectionstatechange = () => {
                const state = this.peerConnection.iceConnectionState;
                console.log('ICE Connection state:', state);

                if (state === 'failed') {
                    console.error('ICE connection failed. Check your network connection.');
                }
            };

            return this.peerConnection;
        } catch (err) {
            console.error('Error creating peer connection:', err);
            throw new Error('Failed to establish call connection. Please try again.');
        }
    }

    // Caller creates SDP Offer
    async createOffer() {
        if (!this.peerConnection) {
            throw new Error('Peer connection not created. Please try again.');
        }

        try {
            const offer = await this.peerConnection.createOffer({
                offerToReceiveAudio: true,
                offerToReceiveVideo: false
            });
            await this.peerConnection.setLocalDescription(offer);
            return offer;
        } catch (err) {
            console.error('Error creating offer:', err);
            throw new Error('Failed to create call offer. Please try again.');
        }
    }

    // Receiver creates SDP Answer
    async createAnswer(offerSdp) {
        if (!this.peerConnection) {
            throw new Error('Peer connection not created. Please try again.');
        }

        try {
            await this.peerConnection.setRemoteDescription(new RTCSessionDescription(offerSdp));
            const answer = await this.peerConnection.createAnswer();
            await this.peerConnection.setLocalDescription(answer);
            return answer;
        } catch (err) {
            console.error('Error creating answer:', err);
            throw new Error('Failed to accept call. Please try again.');
        }
    }

    // Set remote answer on caller side
    async setRemoteAnswer(answerSdp) {
        if (!this.peerConnection) {
            throw new Error('Peer connection not created. Please try again.');
        }

        try {
            if (this.peerConnection.signalingState !== 'stable') {
                await this.peerConnection.setRemoteDescription(new RTCSessionDescription(answerSdp));
            }
        } catch (err) {
            console.error('Error setting remote answer:', err);
            throw new Error('Failed to connect call. Please try again.');
        }
    }

    // Add ICE candidate
    async addIceCandidate(candidate) {
        if (!this.peerConnection || !candidate) return;

        try {
            await this.peerConnection.addIceCandidate(new RTCIceCandidate(candidate));
        } catch (err) {
            console.warn('Error adding ICE candidate:', err);
            // Don't throw, as this is often recoverable
        }
    }

    // Toggle Mute Audio
    toggleMute(isMuted) {
        this.isMuted = isMuted;
        if (this.localStream) {
            this.localStream.getAudioTracks().forEach(track => {
                track.enabled = !isMuted;
            });
        }
    }

    // Check if muted
    getMuteState() {
        return this.isMuted;
    }

    // Check if audio is available
    hasAudio() {
        return this.localStream && this.localStream.getAudioTracks().length > 0;
    }

    // Clean up streams and peer connection
    cleanup() {
        if (this.localStream) {
            this.localStream.getTracks().forEach(track => {
                try {
                    track.stop();
                } catch (err) {
                    // Ignore errors during cleanup
                }
            });
            this.localStream = null;
        }
        if (this.peerConnection) {
            try {
                this.peerConnection.close();
            } catch (err) {
                // Ignore errors during cleanup
            }
            this.peerConnection = null;
        }
        this.remoteStream = null;
        this.isMuted = false;
        this.isInitialized = false;
    }
}

// Export singleton instance
export default new WebRTCService();